/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Text from 'components/shared/Text';
import styles from './styles.module.scss';

let showErrorTimeout;

export function Field(props) {
  const [inputFocused, setInputFocused] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleChange = e => {
    if (showError) setShowError(false);
    clearTimeout(showErrorTimeout);
    props.onChange(e);
    showErrorTimeout = setTimeout(() => setShowError(true), 150);
  };

  const handleFocus = e => {
    setInputFocused(true);
    if (typeof props.onFocus === 'function') {
      props.onBlur(e);
    }
  };

  const handleBlur = e => {
    setInputFocused(false);
    setShowError(true);
    if (typeof props.onBlur === 'function') {
      props.onBlur(e);
    }
  };

  return (
    <div className={classnames([styles.fieldWrapper, ...props.fieldWrapperStyles])}>
      <span
        className={classnames([
          styles.fieldLabel,
          { [styles.hiddenFieldLabel]: props.noLabel || props.inlineLabel }
        ])}>
        <Text tag="label" htmlFor={props.id}>
          {props.name}
        </Text>
        {props.labelInlineElements ?? null}
      </span>
      <div
        className={classnames([
          styles.inputWrapper,
          {
            [styles.focused]: inputFocused,
            [styles.error]: props.error && showError,
            [styles.orangeBorder]: props.passwordStrength === 3,
            [styles.greenBorder]: props.passwordStrength > 3,
            [styles.readOnlyWrapper]: props.readOnly
          },
          ...props.inputWrapperStyles
        ])}>
        {props.inlineLabel && (
          <div className={styles.inlineLabel} style={{ minWidth: props.inlineLabelMinWidth }}>
            <Text tag="label" htmlFor={props.id}>
              {props.name}
            </Text>
          </div>
        )}
        {props.leadingInlineElements ?? null}
        <input
          className={classnames([
            styles.fieldInput,

            {
              [styles.inModal]: props.inModal,
              [styles.readOnlyInput]: props.readOnly
            },
            ...props.inputStyles
          ])}
          id={props.id}
          type={props.type}
          step={props.step}
          pattern={props.pattern}
          inputMode={props.inputMode}
          value={props.value}
          placeholder={props.placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={props.maxLength}
          autoFocus={props.autoFocus}
          ref={props.ref}
          autoComplete={props.autoComplete ? undefined : 'off'}
          onWheelCapture={e => {
            e.target.blur();
          }}
        />
        {props.showPasswordStrength && (
          <div className={styles.strengthMeterContainer}>
            <div
              className={classnames([
                styles.strengthMeter,
                {
                  [styles.compromised]: props.passwordCompromised,
                  [styles.weak]: props.passwordStrength <= 2,
                  [styles.medium]: props.passwordStrength === 3,
                  [styles.quarter]:
                    props.passwordCompromised || Math.max(25, 25 * props.passwordStrength) === 25,
                  [styles.half]: Math.max(25, 25 * props.passwordStrength) === 50,
                  [styles.threeQuarters]: Math.max(25, 25 * props.passwordStrength) === 75,
                  [styles.empty]: props.value.length === 0
                }
              ])}
            />
          </div>
        )}
        {props.trailingInlineElements ?? null}
      </div>
      {props.error && showError && (
        <Text tag="p" styles={[styles.fieldError]}>
          {props.error}
        </Text>
      )}
    </div>
  );
}

Field.propTypes = {
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.bool,
  error: PropTypes.string,
  id: PropTypes.string.isRequired,
  noLabel: PropTypes.bool,
  inlineLabel: PropTypes.bool,
  maxLength: PropTypes.number,
  name: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  passwordCompromised: PropTypes.bool,
  passwordStrength: PropTypes.number,
  showPasswordStrength: PropTypes.bool,
  labelInlineElements: PropTypes.element,
  leadingInlineElements: PropTypes.element,
  trailingInlineElements: PropTypes.element,
  inModal: PropTypes.bool,
  type: PropTypes.string,
  step: PropTypes.string,
  pattern: PropTypes.string,
  inputMode: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ref: PropTypes.shape({ current: PropTypes.any }),
  fieldWrapperStyles: PropTypes.array,
  inputWrapperStyles: PropTypes.array,
  inputStyles: PropTypes.array
};

Field.defaultProps = {
  autoFocus: false,
  autoComplete: true,
  noLabel: undefined,
  inlineLabel: false,
  readOnly: false,
  error: undefined,
  maxLength: undefined,
  onFocus: undefined,
  onBlur: undefined,
  passwordCompromised: false,
  passwordStrength: 0,
  showPasswordStrength: false,
  labelInlineElements: undefined,
  leadingInlineElements: undefined,
  trailingInlineElements: undefined,
  inModal: false,
  type: 'text',
  step: undefined,
  pattern: undefined,
  inputMode: undefined,
  value: '',
  placeholder: undefined,
  ref: undefined,
  fieldWrapperStyles: [],
  inputWrapperStyles: [],
  inputStyles: []
};

export default Field;
